import React from 'react';
import { useNavigate } from 'react-router-dom';
import { workPlanApi, taskApi } from '../../services/api';
import { Badge } from '../ui/badge';
import PageHeader from '../Layout/PageHeader';
import './WorkPlanSummary.css';

const TASK_STATUS = {
  OPEN: 'open',
  IN_PROGRESS: 'in-progress',
  COMPLETED: 'completed',
  CLOSED: 'closed',
  RELEASED: 'released'
};

const WorkPlanSummary = () => {
  const navigate = useNavigate();
  const [workPlan, setWorkPlan] = React.useState(null);
  const [tasks, setTasks] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    loadWorkPlanAndTasks();
  }, []);

  const loadWorkPlanAndTasks = async () => {
    try {
      const [activePlan, allTasks] = await Promise.all([
        workPlanApi.getActive(),
        taskApi.getAll()
      ]);

      if (activePlan) {
        // Combine work plan tasks with their full task data
        const workPlanWithTaskDetails = {
          ...activePlan,
          tasks: activePlan.tasks.map(planTask => {
            const taskData = allTasks.find(t => t.id === planTask.task_id);
            return taskData ? {
              ...taskData,
              ...planTask,
              id: planTask.task_id, // Ensure we use the task_id as the unique key
              workPlanTaskId: planTask.work_plan_task_id,
              status: planTask.status.toLowerCase() || TASK_STATUS.OPEN.toLowerCase(),
              actualHours: planTask.hours || 0,
              plannedHours: planTask.planned_hours || 0
            } : null;
          }).filter(Boolean)
        };
        setWorkPlan(workPlanWithTaskDetails);
      }
      setTasks(allTasks);
    } catch (error) {
      console.error('Failed to load work plan:', error);
      setError('Failed to load work plan data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return (
    <div className="flex w-full flex-col gap-6 px-6 py-6">
      <PageHeader title="Day Summary" />
      <div className="summary-container">
        <div className="loading-container">Loading...</div>
      </div>
    </div>
  );

  if (error) return (
    <div className="flex w-full flex-col gap-6 px-6 py-6">
      <PageHeader title="Day Summary" />
      <div className="summary-container">
        <div className="error-message">{error}</div>
      </div>
    </div>
  );

  if (!workPlan) {
    return (
      <div className="flex w-full flex-col gap-6 px-6 py-6">
        <PageHeader title="Day Summary" />
        <div className="summary-container">
          <div className="no-plan-message">No active work plan found.</div>
        </div>
      </div>
    );
  }

  const totalHours = workPlan.tasks.reduce((sum, task) => sum + (task.actualHours || 0), 0);

  return (
    <div className="flex w-full flex-col gap-6 px-6 py-6">
      <PageHeader title="Day Summary" />
      <div className="summary-container">
        <div className="summary-header">
          <h2>Work Plan Summary</h2>
          <div className="summary-meta">
            <span>Date: {new Date(workPlan.date).toLocaleDateString()}</span>
            <span>Total Hours: {totalHours}</span>
          </div>
        </div>

        <div className="tasks-summary">
          {workPlan.tasks.map(task => (
            <div key={task.id} className="task-summary-card">
              <div className="task-summary-header">
                <h3>{task.title}</h3>
                <Badge variant={task.status.toLowerCase() === TASK_STATUS.IN_PROGRESS.toLowerCase() ? "warning" : undefined}>
                  {task.status}
                </Badge>
              </div>
              
              {task.description && (
                <div className="task-description">
                  <h4>Description</h4>
                  <p>{task.description}</p>
                </div>
              )}

              <div className="task-details">
                <div className="hours-logged">
                  <h4>Hours</h4>
                  <div className="hours-breakdown">
                    <span>Planned: {task.plannedHours || 0}</span>
                    <span>Actual: {task.actualHours || 0}</span>
                  </div>
                </div>

                {task.notes && (
                  <div className="task-notes">
                    <h4>Notes</h4>
                    <p>{task.notes}</p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default WorkPlanSummary; 